/**
 * Field Entities - Betrieb Müller
 *
 * Agricultural fields (Schläge) as Digital Twin entities.
 */

import type { FieldEntity } from '@hofsync/design-system';
import { yearsAgo, daysAgo } from '../farm-profile';

/**
 * Großes Feld - Main field with expired soil sample
 */
export const FIELD_GROSSES_FELD: FieldEntity = {
  id: 'field-grosses-feld',
  type: 'field',
  name: 'Großes Feld',
  status: 'warning',
  lastUpdated: daysAgo(2),
  properties: {
    land_type: 'arable',
    size: '12,4 ha',
    usage: 'Winterweizen',
    soilType: 'sandiger Lehm',
    lastSoilSample: yearsAgo(6).toISOString().split('T')[0], // March 2019 - expired
    nRequirement: '180 kg N/ha',
    nApplied: '80 kg N/ha',
    phosphorusClass: 'C',
    feldblockNr: 'DENILI0340123456',
  },
  documents: [
    {
      id: 'doc-bp-2019-grosses-feld',
      name: 'Bodenprobe - Großes Feld',
      type: 'bodenprobe',
      date: yearsAgo(6),
      source: 'LUFA Nord-West',
      status: 'expired',
      referenceNumber: 'BP-2019-4523',
      extractedData: {
        pH: 6.2,
        P2O5: '18 mg/100g',
        K2O: '22 mg/100g',
        Mg: '8 mg/100g',
        pKlasse: 'C',
        kKlasse: 'C',
        mgKlasse: 'B',
      },
      relatedEntityIds: ['field-grosses-feld'],
      satisfiesRequirements: ['duev-4-4'],
    },
  ],
};

/**
 * Hinterfeld - Field with current soil sample
 */
export const FIELD_HINTERFELD: FieldEntity = {
  id: 'field-hinterfeld',
  type: 'field',
  name: 'Hinterfeld',
  status: 'active',
  lastUpdated: daysAgo(5),
  properties: {
    land_type: 'arable',
    size: '8,7 ha',
    usage: 'Wintergerste',
    soilType: 'lehmiger Sand',
    lastSoilSample: yearsAgo(2).toISOString().split('T')[0], // March 2024 - valid
    nRequirement: '160 kg N/ha',
    nApplied: '60 kg N/ha',
    phosphorusClass: 'C',
    feldblockNr: 'DENILI0340123457',
  },
  documents: [
    {
      id: 'doc-bp-2024-hinterfeld',
      name: 'Bodenprobe - Hinterfeld',
      type: 'bodenprobe',
      date: yearsAgo(2),
      source: 'LUFA Nord-West',
      status: 'valid',
      referenceNumber: 'BP-2024-1287',
      extractedData: {
        pH: 6.0,
        P2O5: '15 mg/100g',
        K2O: '19 mg/100g',
        Mg: '7 mg/100g',
        pKlasse: 'C',
        kKlasse: 'C',
        mgKlasse: 'B',
      },
      relatedEntityIds: ['field-hinterfeld'],
      satisfiesRequirements: ['duev-4-4'],
    },
  ],
};

/**
 * Moorwiese - Grassland field
 */
export const FIELD_MOORWIESE: FieldEntity = {
  id: 'field-moorwiese',
  type: 'field',
  name: 'Moorwiese',
  status: 'active',
  lastUpdated: daysAgo(10),
  properties: {
    land_type: 'grassland',
    size: '5,2 ha',
    usage: 'Dauergrünland',
    soilType: 'Niedermoor',
    lastSoilSample: yearsAgo(1).toISOString().split('T')[0],
    nRequirement: '120 kg N/ha',
    nApplied: '40 kg N/ha',
    phosphorusClass: 'B',
    feldblockNr: 'DENILI0340123458',
  },
  documents: [
    {
      id: 'doc-bp-2025-moorwiese',
      name: 'Bodenprobe - Moorwiese',
      type: 'bodenprobe',
      date: yearsAgo(1),
      source: 'LUFA Nord-West',
      status: 'valid',
      referenceNumber: 'BP-2025-0892',
      extractedData: {
        pH: 5.4,
        P2O5: '12 mg/100g',
        K2O: '16 mg/100g',
        Mg: '10 mg/100g',
        pKlasse: 'B',
        kKlasse: 'C',
        mgKlasse: 'C',
      },
      relatedEntityIds: ['field-moorwiese'],
      satisfiesRequirements: ['duev-4-4'],
    },
  ],
};

/**
 * Waldacker - Field near forest edge
 */
export const FIELD_WALDACKER: FieldEntity = {
  id: 'field-waldacker',
  type: 'field',
  name: 'Waldacker',
  status: 'warning',
  lastUpdated: daysAgo(3),
  properties: {
    land_type: 'arable',
    size: '6,8 ha',
    usage: 'Mais',
    soilType: 'sandiger Lehm',
    lastSoilSample: yearsAgo(5).toISOString().split('T')[0], // Approaching 6-year limit
    nRequirement: '200 kg N/ha',
    nApplied: '0 kg N/ha',
    phosphorusClass: 'D',
    feldblockNr: 'DENILI0340123459',
  },
  documents: [
    {
      id: 'doc-bp-2021-waldacker',
      name: 'Bodenprobe - Waldacker',
      type: 'bodenprobe',
      date: yearsAgo(5),
      source: 'LUFA Nord-West',
      status: 'expiring', // Will expire this year
      expiresAt: new Date('2026-03-20'),
      referenceNumber: 'BP-2021-3456',
      extractedData: {
        pH: 6.1,
        P2O5: '25 mg/100g',
        K2O: '20 mg/100g',
        Mg: '9 mg/100g',
        pKlasse: 'D',
        kKlasse: 'C',
        mgKlasse: 'B',
      },
      relatedEntityIds: ['field-waldacker'],
      satisfiesRequirements: ['duev-4-4'],
    },
  ],
};

/**
 * All field entities
 */
export const ALL_FIELDS: FieldEntity[] = [
  FIELD_GROSSES_FELD,
  FIELD_HINTERFELD,
  FIELD_MOORWIESE,
  FIELD_WALDACKER,
];

/**
 * Get field by ID
 */
export function getFieldById(id: string): FieldEntity | undefined {
  return ALL_FIELDS.find((field) => field.id === id);
}

/**
 * Get fields with expired or expiring soil samples
 */
export function getFieldsNeedingSoilSample(): FieldEntity[] {
  return ALL_FIELDS.filter((field) => {
    const docs = field.documents.filter((d) => d.type === 'bodenprobe');
    return docs.some((d) => d.status === 'expired' || d.status === 'expiring');
  });
}
